import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowsOutSimple, Plus, Sparkle, X } from '@phosphor-icons/react';
import { useAiAssistantAccess } from '@/api/hooks/useAiAssistant';
import { AssistantComposer } from '@/components/assistant/composer';
import { AssistantMessageList } from '@/components/assistant/message-list';
import { useAssistantChat } from '@/components/assistant/use-assistant-chat';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const OPEN_KEY = 'wms.assistant.widget.open';

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOpen(open: boolean): void {
  try {
    localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    // per-viewer convenience only
  }
}

/**
 * Bottom-right launcher for the WMS Analyst, available on every page to
 * anyone the assistant admits. Shares conversations with the full
 * /assistant page through the same per-browser store. Hidden on /assistant
 * itself (the page is the big version of the same thing).
 */
export function AssistantWidget() {
  const { pathname } = useLocation();
  const access = useAiAssistantAccess();
  const allowed = access.data?.allowed === true;
  const onAssistantPage = pathname.startsWith('/assistant');

  if (!allowed || onAssistantPage) return null;
  return <WidgetBody notConfigured={access.data?.configured === false} />;
}

function WidgetBody({ notConfigured }: { notConfigured: boolean }) {
  const [open, setOpen] = useState<boolean>(() => readOpen());
  const chat = useAssistantChat();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);

  const toggle = useCallback(
    (next: boolean) => {
      setOpen(next);
      writeOpen(next);
      if (next) chat.reload();
    },
    [chat],
  );

  // Pick up threads the full page wrote before this instance appeared.
  useEffect(() => {
    chat.reload();
  }, []);

  const panelRef = useRef<HTMLDivElement>(null);
  // Esc closes the panel, but only when focus is inside it (a page dialog's
  // own Esc must not close the widget behind it).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (panelRef.current?.contains(document.activeElement)) toggle(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, toggle]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight });
    }
  }, []);
  useEffect(() => {
    if (open && atBottom) scrollToBottom();
  }, [open, atBottom, chat.active?.messages, scrollToBottom]);

  const send = (text: string) => {
    setAtBottom(true);
    chat.send(text);
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          type="button"
          data-testid="assistant-widget-launcher"
          aria-label="Ask the WMS Analyst"
          title="Ask the WMS Analyst"
          onClick={() => toggle(true)}
          className="fixed bottom-[calc(var(--mobile-nav-h)+1rem)] right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:bottom-6 lg:right-6"
        >
          <Sparkle weight="fill" className="h-5 w-5" />
          {chat.streaming && (
            <span className="absolute -right-0.5 -top-0.5 h-3 w-3 animate-pulse rounded-full border-2 border-background bg-warning" />
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="WMS Analyst"
          data-testid="assistant-widget-panel"
          className={cn(
            'fixed z-50 flex flex-col overflow-hidden border bg-background shadow-2xl',
            // Phone: full-screen above the bottom nav. Desktop: a corner panel.
            'inset-x-0 top-0 bottom-[var(--mobile-nav-h)] rounded-none',
            // Tablets still show the bottom nav (it hides at lg), so the corner
            // panel sits above it until lg.
            'sm:inset-auto sm:bottom-[calc(var(--mobile-nav-h)+1rem)] sm:right-6 sm:h-[min(640px,calc(100dvh-var(--mobile-nav-h)-2rem))] sm:w-[400px] sm:rounded-2xl',
            'lg:bottom-6 lg:h-[min(640px,calc(100dvh-3rem))]',
          )}
        >
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15">
              <Sparkle weight="fill" className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold leading-tight">
                WMS Analyst
              </div>
              <div className="truncate text-[11px] leading-tight text-muted-foreground">
                {chat.active?.title ??
                  'Ask about live data or how the system works'}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-0.5">
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                title="New conversation"
                aria-label="New conversation"
                onClick={() => {
                  chat.newConversation();
                  setAtBottom(true);
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
              {chat.streaming ? (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  title="Wait for the answer to finish before opening the full page"
                  aria-label="Open full page (available when the answer finishes)"
                  disabled
                >
                  <ArrowsOutSimple className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  asChild
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  title="Open full page"
                  aria-label="Open full page"
                >
                  <Link to="/assistant" onClick={() => toggle(false)}>
                    <ArrowsOutSimple className="h-4 w-4" />
                  </Link>
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                title="Close"
                aria-label="Close"
                onClick={() => toggle(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div
            ref={scrollRef}
            onScroll={() => {
              const el = scrollRef.current;
              if (!el) return;
              setAtBottom(
                el.scrollHeight - el.scrollTop - el.clientHeight < 80,
              );
            }}
            className="min-h-0 flex-1 overflow-y-auto px-3 py-3"
          >
            {!chat.active || chat.active.messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15">
                  <Sparkle weight="fill" className="h-5 w-5 text-primary" />
                </div>
                <p className="mt-3 text-sm font-semibold">
                  Ask the WMS anything
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  How a process works, where a setting lives, or what the live
                  data says. Answers cite the spec and the queries they ran.
                </p>
                {chat.conversations.length > 0 && (
                  <div className="mt-4 w-full text-left">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Recent
                    </div>
                    {chat.conversations.slice(0, 4).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          chat.selectConversation(c.id);
                          setAtBottom(true);
                        }}
                        className="block w-full truncate rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted"
                      >
                        {c.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <AssistantMessageList
                messages={chat.active.messages}
                streamingMessageId={chat.streamingMessageId}
                compact
                conversationId={chat.active.id}
                onFeedback={(messageId, rating) =>
                  chat.active &&
                  chat.setFeedback(chat.active.id, messageId, rating)
                }
                onRetry={(messageId) => {
                  setAtBottom(true);
                  if (chat.active) chat.retry(chat.active.id, messageId);
                }}
              />
            )}
          </div>

          <div className="border-t px-3 py-2">
            <AssistantComposer
              onSend={send}
              onStop={chat.stop}
              streaming={chat.streaming}
              disabled={notConfigured}
              placeholder="Ask the WMS Analyst…"
            />
          </div>
        </div>
      )}
    </>
  );
}
